// ── THE FULL STRATEGY REPORT, SYNTHESISED ────────────────────────────────────
//
// Kevin, 2026-09-16: "Turn Full Strategy Report into a polished, simple report an
// agent can hand to a farmer and explain in 2–3 minutes." The report must answer, in
// this order:
//
//     1. What are you recommending?
//     2. What does it cost?
//     3. How is that different from what I have now?
//     4. Why are you recommending the change, or keeping the current coverage?
//     5. What information is still missing?
//
// ── WHY THIS LAYER EXISTS ────────────────────────────────────────────────────
//
// V1 was engines → each engine's own narrative → concatenated into a PDF. For DU MOND
// AG LLC that produced 15 printed sheets (19 with every section on), the acreage
// request three times, and the Cayuga block saying "the current election stands"
// beside a recommendation to move 75% → 80%. Nothing was miscalculated. The engine's
// internal reasoning was handed to the farmer almost verbatim.
//
// So the architecture is now
//
//     engines → verified STRUCTURED results → this synthesis → the customer document
//
// ⚠ THIS MODULE COMPUTES NO COVERAGE. APH, guarantees, premiums, SCO/ECO, prevent
// plant, historical band values and the recommendation classification all arrive
// already computed — from wheatQuotePages, the one function that prices the printed
// quote, through its `factSink`. What happens here is presentation arithmetic ONLY:
// the difference between two per-acre figures the engines produced, and the width of
// a band in points. Anything that is not already in a fact stays null and prints as
// pending; nothing is estimated here to fill it.
//
// ⚠ IT NEVER PASSES ENGINE PROSE THROUGH. `rec.why`, `whyNot85`, `bandDecision.why`
// and the trace are the engine explaining itself to an agent. Every sentence below is
// built from the engine's STRUCTURED fields (band evidence, expected value, premium,
// years) in the grower's terms, and customerLanguage.js is the backstop that fails the
// build if an internal term leaks anyway.
//
// It is import-light on purpose — two sibling modules, the jargon list and the ARC/PLC
// engine whose label it prints — so tools/check-strategy-report can load and drive the
// SHIPPED file.

import { findJargon } from "./customerLanguage.js";
// ⚠ THE .js IS LOAD-BEARING — webpack resolves an extensionless specifier and
// node's ESM loader does not, and this module is imported directly by node in
// tools/check-strategy-report.js.
import { electionOutlook, electionLabel, BELOW_SWITCH_ARC } from "./arcPlcElection.js";

// Engine vocabulary this module READS. Mirrored here as plain strings (not imported)
// so the synthesis can be loaded without the wheat engine; the checker asserts they
// still equal the engine's own constants, so a rename there cannot silently turn every
// band into "unknown" here.
export const ENGINE = {
  CLASS: { AGENT: "AGENT_SELECTED", KEEP: "KEEP_CURRENT", C75: "CONSIDER_75", C80: "CONSIDER_80",
           C85: "CONSIDER_85", INSUFFICIENT: "INSUFFICIENT_EVIDENCE_TO_CHANGE", ACREAGE: "ACREAGE_REQUIRED" },
  EVID: { SUPPORTS: "CLEARLY_SUPPORTS", BORDERLINE: "BORDERLINE", NOT: "CLEARLY_DOES_NOT_SUPPORT", UNTESTABLE: "UNTESTABLE" },
  ACRE: { REPORTED: "REPORTED", PLANNED: "PLANNED", PRIOR_YEAR: "PRIOR_YEAR", ASSUMED: "ASSUMED", UNKNOWN: "UNKNOWN" },
};

// What the base MPCI line is doing. KEEP is ONLY the base level — never the strategy.
export const BASE_ACTION = { KEEP: "KEEP", INCREASE: "INCREASE", DECREASE: "DECREASE", NEW: "NEW" };
// What each area endorsement is doing, separately.
export const AREA_ACTION = { ADD: "ADD", KEEP: "KEEP", DROP: "DROP", NONE: "NONE" };

const EPS = 1e-9;
const isNum = (v) => v != null && v !== "" && Number.isFinite(Number(v));
const num = (v) => (isNum(v) ? Number(v) : null);
const pct = (f) => (isNum(f) ? Math.round(Number(f) * 100) : null);
const same = (a, b) => isNum(a) && isNum(b) && Math.abs(Number(a) - Number(b)) < EPS;
const round2 = (v) => (isNum(v) ? Math.round(Number(v) * 100) / 100 : null);
// TOTAL COVERAGE = the farm's own layer PLUS the county layers above it, which is the
// number a grower compares year to year. ⚠ A NULL MPCI RETURNS NULL, never 0 — a layer
// nobody priced is not a layer worth nothing — while a null SCO or ECO on a priced MPCI
// is a layer the grower does not carry and contributes 0.
const sumLayers = (c) => (c && isNum(c.mpci)
  ? round2(Number(c.mpci) + (isNum(c.sco) ? Number(c.sco) : 0) + (isNum(c.eco) ? Number(c.eco) : 0))
  : null);

// ── money, as a grower reads it ──────────────────────────────────────────────
// ⚠ A MISSING FIGURE IS NEVER $0. A null premium printed as "$0.00/ac" reads as
// "this coverage is free", which is the opposite of "we could not price it".
export function money(v, { perAcre = false, signed = false } = {}) {
  if (!isNum(v)) return null;
  const n = Number(v);
  const abs = "$" + Math.abs(n).toFixed(2);
  const sign = signed ? (n > 0.004 ? "+" : n < -0.004 ? "−" : "") : (n < 0 ? "−" : "");
  return sign + abs + (perAcre ? "/ac" : "");
}
export const PENDING = "Pending";

export const UNIT_NAME = { EU: "Enterprise unit", BU: "Basic unit", OU: "Optional units", UA: "Unit arrangement" };
export const titleCounty = (c) => String(c || "").toLowerCase().replace(/\b[a-z]/g, (m) => m.toUpperCase());

// ── THE UNIT STRUCTURE, WITH ITS SOURCE ──────────────────────────────────────
// ⚠ A PREMIUM IS NEVER QUOTED ON A UNIT STRUCTURE NOBODY STATED. The enterprise-unit
// discount is the largest term in a wheat premium: DU MOND AG LLC's Wayne wheat read
// $22.13/ac at enterprise-unit rates where its AIP record says basic unit ($34.22).
// `u.unit` carries the structure and the crop year the policy stated it for; where it is
// null the report says the structure is not on file and quotes no premium for that county.
// A fact built before the unit source was carried (no `unit` key) reads the proposed unit.
export function unitInfo(u) {
  const x = u && u.unit;
  if (!x) return { known: true, unit: (u && u.proposed && u.proposed.unit) || null, year: null };
  return x.value ? { known: true, unit: x.value, year: isNum(x.year) ? Number(x.year) : null }
                 : { known: false, unit: null, year: null };
}
// ⚠ A PREMIUM NEEDS A UNIT STRUCTURE *AND* AN ACREAGE SOMEBODY SUPPLIED (2026-09-18). An
// ASSUMED acreage is the quoter's invented default, and the per-acre premium is not
// acre-invariant (the BU/EU discount band moves it), so a premium figured on it is not a
// figure for this farm. Metzler's Harford wheat printed "the per-acre premium is figured on
// 100 ac". Withheld exactly like a missing unit structure — same gates, same "Not quoted" —
// with its own reason. Guarantee and coverage do not depend on acreage and still print.
export function premiumBasisOf(u) {
  if (!unitInfo(u).known) return { ok: false, why: "unit structure not on file" };
  if (u && u.acreage && u.acreage.source === ENGINE.ACRE.ASSUMED)
    return { ok: false, why: "premium pending — no wheat acres on record" };
  return { ok: true, why: null };
}
export function unitText(u) {
  const i = unitInfo(u);
  if (!i.known) return "unit structure not on file";
  if (!i.unit) return null;
  return (UNIT_NAME[i.unit] || i.unit) + (i.year ? " (your " + i.year + " policy)" : "");
}

// ── 1. WHAT THE BASE COVERAGE IS DOING ───────────────────────────────────────
export function baseAction(u) {
  const cur = u && u.current ? num(u.current.level) : null;
  const prop = u && u.proposed ? num(u.proposed.level) : null;
  const plan = (u && u.proposed && u.proposed.plan) || "RP";
  const unit = unitText(u);
  const product = (plan === "RP" ? "Revenue Protection" : plan) + (unit ? " · " + unit : "");
  if (prop == null) return { kind: null, from: pct(cur), to: null, text: PENDING, product };
  if (cur == null) return { kind: BASE_ACTION.NEW, from: null, to: pct(prop), text: pct(prop) + "%", product };
  if (same(cur, prop)) return { kind: BASE_ACTION.KEEP, from: pct(cur), to: pct(prop), text: "Keep " + pct(prop) + "%", product };
  return { kind: prop > cur ? BASE_ACTION.INCREASE : BASE_ACTION.DECREASE, from: pct(cur), to: pct(prop),
           text: "Move " + pct(cur) + "% → " + pct(prop) + "%", product };
}

// ── 2. WHAT EACH AREA ENDORSEMENT IS DOING ───────────────────────────────────
// The band, and the share of it the grower actually buys. RMA sells SCO/ECO at 50-100% of
// the band; below 100% the band alone overstates what the endorsement pays. `elected` null
// means "not stated", which prints the band alone rather than inventing 100%.
export function bandShareText(elected) {
  const p = num(elected);
  return p != null && p < 100 - 1e-9 ? " at " + (Number.isInteger(p) ? p : p.toFixed(2)) + "%" : "";
}
function bandText(name, bands, baseLevel, elected) {
  if (!bands) return name;
  if (name === "SCO") return "SCO (" + pct(baseLevel) + "–" + pct(bands.scoTop) + "%)" + bandShareText(elected);
  return "ECO (" + pct(bands.ecoBottom) + "–" + pct(bands.ecoTop) + "%)" + bandShareText(elected);
}
export function areaAction(u) {
  const cur = (u && u.current) || {};
  const prop = (u && u.proposed) || {};
  const b = u && u.bands2027;
  const one = (k) => {
    const had = !!cur[k], has = !!prop[k];
    return had && has ? AREA_ACTION.KEEP : !had && has ? AREA_ACTION.ADD : had && !has ? AREA_ACTION.DROP : AREA_ACTION.NONE;
  };
  const sco = one("sco"), eco = one("eco");
  const verb = { ADD: "Add", KEEP: "Keep", DROP: "Drop" };
  const parts = [];
  // A DROP describes what they HAVE; anything else describes what they would hold.
  const share = (k) => (one(k) === AREA_ACTION.DROP ? cur[k + "Pct"] : prop[k + "Pct"]);
  if (sco !== AREA_ACTION.NONE) parts.push(verb[sco] + " " + bandText("SCO", b, prop.level, share("sco")));
  if (eco !== AREA_ACTION.NONE) parts.push(verb[eco] + " " + bandText("ECO", b, prop.level, share("eco")));
  return { sco, eco, text: parts.length ? parts.join(" · ") : "None", lines: parts.length ? parts : ["None"],
           changes: sco === AREA_ACTION.ADD || sco === AREA_ACTION.DROP || eco === AREA_ACTION.ADD || eco === AREA_ACTION.DROP };
}

// ── 3. THE BAND EVIDENCE, IN THE GROWER'S TERMS ──────────────────────────────
//
// The engine walks up from 75% one 5-point step at a time and records, per step,
// what the step adds to the premium (`margPrem`), what it would have paid on this
// farm's own yield record (`expIndemnity`, averaged over `nYears`), and how steady
// that answer is when any single year is removed (`evidence`). These are those same
// numbers, said plainly. Nothing is recomputed.
const bandAt = (u, from) => {
  const bands = (u && u.engine && u.engine.bandDecision && u.engine.bandDecision.bands) || [];
  return bands.find((x) => same(x.from, from)) || null;
};
const stepName = (from, to) => pct(from) + "% to " + pct(to) + "%";
const yearsTxt = (n) => n + " year" + (n === 1 ? "" : "s");

// ⚠ "WOULD HAVE PAID" IS A REPLAY, AND SAYS ON WHAT. The band value is each year's yield
// on the farm's APH record, applied at TODAY's APH and projected price (bandRules
// bandExperience) — not a payment the policy made. The records are the APH database's
// years, which is why they are called records rather than harvests.
// A step UP the grower is being asked to buy, and why it earns its place.
function stepEarned(b) {
  if (b.free) return "moving from " + stepName(b.from, b.to) + " adds no cost overall, because it replaces county-coverage premium you would otherwise pay";
  return "moving to " + pct(b.to) + "% puts more of your protection on your own farm's yield and revenue. Replaying your " + yearsTxt(b.nYears) +
    " of wheat yield records at today's APH and price, the " + pct(b.from) + "–" + pct(b.to) + "% layer would have paid an average of " + money(b.expIndemnity, { perAcre: true }) +
    " a year, against " + money(b.margPrem, { perAcre: true }) + " of added premium";
}
// Why the NEXT step up is not recommended.
function stepNotEarned(b) {
  if (!b) return null;
  if (!b.priceable) return "RMA does not offer the " + pct(b.to) + "% level for this county and farming practice";
  const cost = money(b.margPrem, { perAcre: true });
  const val = money(b.expIndemnity, { perAcre: true });
  if (b.marginShort)
    return "moving to " + pct(b.to) + "% would cost another " + cost + ". It would have roughly paid for itself on your history (about " +
      val + " a year), but not by a clear margin — these are the most expensive 5 points of coverage, because the government pays the smallest share of them";
  if (b.evidence === ENGINE.EVID.BORDERLINE)
    return "moving to " + pct(b.to) + "% would cost another " + cost + ". Your records show some value (about " + val +
      " a year), but mostly from one or two years — not consistent enough to justify the added premium";
  if (b.evidence === ENGINE.EVID.NOT && Number(b.yearsPaying) === 0)
    return "moving to " + pct(b.to) + "% would cost another " + cost + ", and across " + yearsTxt(b.nYears) +
      " of your wheat yield records that extra coverage would not have paid in any year";
  if (b.evidence === ENGINE.EVID.NOT)
    return "moving to " + pct(b.to) + "% would cost another " + cost + ", and across " + yearsTxt(b.nYears) +
      " of your yield records it would have paid only about " + val + " a year" + (isNum(b.yearsPaying) ? " (in " + b.yearsPaying + " of " + b.nYears + " years)" : "");
  return null;
}
// Why the step BELOW the recommendation is kept rather than dropped.
function stepKept(b, u) {
  if (!b) return null;
  if (b.ppHeld) return "your farm has used prevented-planting coverage, and a lower coverage level would lower that guarantee too";
  if (b.free) return "dropping to " + pct(b.from) + "% would not save anything overall";
  if (b.evidence === ENGINE.EVID.SUPPORTS)
    return "dropping to " + pct(b.from) + "% would save " + money(b.margPrem, { perAcre: true }) + " but give up protection that would have paid about " +
      money(b.expIndemnity, { perAcre: true }) + " a year on your farm";
  if (b.evidence === ENGINE.EVID.BORDERLINE || b.marginShort)
    return "your history does not show clearly enough that dropping to " + pct(b.from) + "% would save more than it gives up, so we are not proposing to reduce coverage you already carry";
  return null;
}

// One plain result per coverage step, for the history table. `tone` drives colour only.
export function stepResult(b) {
  if (!b || !b.priceable) return { text: "Not offered here", tone: "meh" };
  if (b.free) return { text: "No added cost", tone: "good" };
  if (b.marginShort) return { text: "Paid for itself, but not by a clear margin", tone: "meh" };
  if (b.evidence === ENGINE.EVID.SUPPORTS) return { text: "Paid for itself", tone: "good" };
  if (b.evidence === ENGINE.EVID.BORDERLINE) return { text: "Not consistent enough", tone: "meh" };
  if (b.evidence === ENGINE.EVID.NOT) return { text: "Did not pay for itself", tone: "no" };
  return { text: "Not enough history", tone: "meh" };
}

// The count of yield years the engine judged against. Taken off the bands it priced,
// or its own record summary — never counted again here.
//
// ⚠ ONE COUNT FOR EVERY PAGE. The first V2 render said "2 years of your wheat yields" on
// page one for Wayne County and "No wheat yield history is on file" on page three: page
// three read the count off the priced bands, and a short history prices none. Every
// sentence and chart now reads `yieldYears` off the recommendation.
export function yieldYears(u) {
  const e = (u && u.engine) || {};
  const b = ((e.bandDecision && e.bandDecision.bands) || []).find((x) => isNum(x.nYears));
  if (b) return Number(b.nYears);
  return isNum(e.yieldYears) ? Number(e.yieldYears) : null;
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export function reasonsFor(u) {
  const e = (u && u.engine) || {};
  const cls = e.classification;
  const bd = e.bandDecision || null;
  const base = baseAction(u);
  const prop = u && u.proposed ? num(u.proposed.level) : null;
  const n = yieldYears(u);
  const next = e.nextLevelUp && isNum(e.nextLevelUp.netCostPerAc) ? e.nextLevelUp : null;
  const out = { why: null, whyNotHigher: null, whyNotLower: null, higherLevel: null, lowerLevel: null, evidence: null };
  if (u && u.priceMissing) {
    out.why = "This county will be priced once RMA announces its " + (u.cropYear || "") + " price for this farming practice.";
    out.evidence = "price-pending";
    return out;
  }
  if (prop == null) return out;
  out.higherLevel = prop < 0.85 - EPS ? pct(prop + 0.05) : null;
  out.lowerLevel = prop > 0.75 + EPS ? pct(prop - 0.05) : null;
  // No unit structure on file: every premium figure for this county is withheld, so no
  // reason may cite one.
  const unitKnown = unitInfo(u).known;

  if (u.selectedBy === "agent" || cls === ENGINE.CLASS.AGENT) {
    out.why = "Your agent selected this coverage level with you.";
    out.evidence = "agent";
    if (premiumBasisOf(u).ok && next && out.higherLevel) out.whyNotHigher = cap(out.higherLevel + "% would cost another " + money(next.netCostPerAc, { perAcre: true }) + ".");
    return out;
  }
  if (cls === ENGINE.CLASS.ACREAGE || e.premiumWithheld) {
    out.why = "Your coverage level stays at " + pct(prop) + "% until your " + (u && isNum(u.cropYear) ? u.cropYear + " " : "") + "wheat acres are known — the cost of each coverage level depends on them.";
    out.evidence = "acreage";
    return out;
  }
  if (bd && bd.belowRange) {
    out.why = "Your " + base.from + "% coverage level is below the 75–85% range we compare, so we have not proposed changing it.";
    out.evidence = "below-range";
    return out;
  }
  if (bd && bd.shortHistory || cls === ENGINE.CLASS.INSUFFICIENT) {
    const years = n || 0;
    // ⚠ "WE HAVE 11 YEARS … THAT IS NOT ENOUGH" IS THE WRONG SENTENCE, and it is
    // what Sullivan Farms' Livingston page printed. Eleven years IS a lot; what
    // is missing is LOSS years — the engine refuses a change because the county
    // trigger has never been tested against a shortfall on this farm, and that
    // needs three. Telling a grower his eleven years are insufficient invites
    // "then how many do you need", and the true answer is a different number
    // about a different thing.
    const ref = bd && bd.downRefused;
    const lossYears = ref && ref.unmeasured && isNum(ref.lossYears) ? ref.lossYears : null;
    out.why = (lossYears != null
      ? "We have " + yearsTxt(years) + " of wheat yield records for this county, but only "
        + (lossYears === 0 ? "no years" : yearsTxt(lossYears))
        + " where your yield fell far enough to test whether the county's own trigger would have covered you. "
        + "Without that we can't show a lower coverage level is safe, so we recommend keeping " + pct(prop) + "%."
      : years
      ? "We have " + yearsTxt(years) + " of wheat yield records for this county. That is not enough to show a different coverage level would pay for itself, so we recommend keeping " + pct(prop) + "%."
      : "No usable wheat yield history is on file for this county, so we can't show a different coverage level would pay for itself. We recommend keeping " + pct(prop) + "%.");
    if (next && out.higherLevel)
      out.whyNotHigher = premiumBasisOf(u).ok
        ? cap(out.higherLevel + "% would cost another " + money(next.netCostPerAc, { perAcre: true }) +
            ", and there isn't enough history yet to show it would earn that back.")
        : "There isn't enough history yet to show " + out.higherLevel + "% would earn back its added premium.";
    out.evidence = "short-history";
    return out;
  }
  if (!unitKnown) {
    out.why = "Coverage levels are compared on what each one costs, and that depends on your unit structure for this county, which is not on file — so " +
      pct(prop) + "% is not a firm recommendation yet.";
    out.evidence = "unit";
    return out;
  }
  if (bd && bd.ppHeld) {
    out.why = "Your farm has used prevented-planting coverage, and lowering your coverage level would lower that guarantee too.";
    out.evidence = "pp";
  }

  // The step(s) between where the grower is and where we recommend.
  if (!out.why) {
    if (base.kind === BASE_ACTION.INCREASE) {
      // Every step between the current level and the recommendation, lowest first —
      // a two-step move is two separately earned steps, and both are said.
      const steps = [];
      for (let lv = num(u.current.level); lv < prop - EPS; lv += 0.05) { const b = bandAt(u, lv); if (b && b.priceable) steps.push(stepEarned(b)); }
      out.why = steps.length ? cap(steps.join("; and ")) + "." : null;
    } else if (base.kind === BASE_ACTION.DECREASE) {
      const steps = [];
      for (let lv = prop; lv < num(u.current.level) - EPS; lv += 0.05) {
        const b = bandAt(u, lv);
        if (b && b.priceable) steps.push("replaying " + yearsTxt(b.nYears) + " of your wheat yield records at today's APH and price, the protection from " + stepName(b.from, b.to) +
          " would have paid about " + money(b.expIndemnity, { perAcre: true }) + " a year — less than the " +
          money(b.margPrem, { perAcre: true }) + " it adds to your premium");
      }
      out.why = steps.length ? cap(steps.join("; and ")) + "." : null;
    } else {
      // KEEP (or new business at the level the walk reached): the reason is what
      // stopped the walk going higher, and — above 75% — what holds the lower step.
      const below = out.lowerLevel != null ? bandAt(u, prop - 0.05) : null;
      const kept = below ? stepKept(below, u) : null;
      const above = out.higherLevel != null ? bandAt(u, prop) : null;
      const stop = above ? stepNotEarned(above) : null;
      out.why = kept ? cap(kept) + "." : stop ? cap(stop) + "." : null;
    }
  }
  // A move DOWN is itself the answer to "why not higher"; a move UP already explains
  // the step below it. Each question is asked only where it is a different question.
  if (out.higherLevel != null && !out.whyNotHigher && base.kind !== BASE_ACTION.DECREASE) {
    const above = bandAt(u, prop);
    const t = above ? stepNotEarned(above) : null;
    if (t && (!out.why || out.why.toLowerCase().indexOf(t.slice(0, 40).toLowerCase()) < 0)) out.whyNotHigher = cap(t) + ".";
    else if (!above && next) out.whyNotHigher = cap(out.higherLevel + "% would cost another " + money(next.netCostPerAc, { perAcre: true }) + ", and the step below it did not pay for itself on your history.");
  }
  if (out.lowerLevel != null && !out.whyNotLower && base.kind !== BASE_ACTION.INCREASE) {
    const below = bandAt(u, prop - 0.05);
    const t = below ? stepKept(below, u) : null;
    if (t && (!out.why || out.why.toLowerCase().indexOf(t.slice(0, 40).toLowerCase()) < 0)) out.whyNotLower = cap(t) + ".";
  }
  if (!out.evidence) out.evidence = n ? "history" : "none";
  if (!out.why) out.why = "We recommend " + pct(prop) + "% based on your coverage options" + (u && isNum(u.cropYear) ? " for " + u.cropYear : "") + ".";
  return out;
}

// ── 4. CURRENT VS PROPOSED, PER ACRE ─────────────────────────────────────────
const areaPoints = (level, flags, bands) => {
  if (!bands || !isNum(level)) return { sco: 0, eco: 0, total: 0 };
  const sco = flags.sco ? Math.max(0, pct(bands.scoTop) - pct(level)) : 0;
  const eco = flags.eco ? Math.max(0, pct(bands.ecoTop) - pct(bands.ecoBottom)) : 0;
  return { sco, eco, total: sco + eco };
};
// ⚠ THE CHANGE IS SPLIT INTO THE LAYERS THAT ACTUALLY MOVE, EACH PRICED AS PROPOSED.
// The first V2 split priced SCO at the CURRENT level (75–90%) and the level step net of
// that SCO shrinking, so Cayuga's card read "SCO +$7.89/ac" for an SCO that is 80–90% in
// the proposal and costs $6.09/ac. Each part here is one line of the proposed stack
// against the same line of the current one, and the parts must sum to the change or no
// split is shown.
export function changeParts(u) {
  const cur = (u && u.current) || {};
  const prop = (u && u.proposed) || {};
  const at = cur.at2027 || {};
  const pp = prop.premium || {};
  const b27 = u && u.bands2027;
  if (!isNum(at.mpci) || !isNum(pp.mpci) || !isNum(at.total) || !isNum(pp.total)) return null;
  const parts = [];
  if (!same(cur.level, prop.level) || Math.abs(Number(pp.mpci) - Number(at.mpci)) >= 0.005)
    parts.push({ label: (same(cur.level, prop.level) ? "" : pct(cur.level) + "→") + pct(prop.level) + "% RP", amount: round2(pp.mpci - at.mpci) });
  for (const k of ["sco", "eco"]) {
    const had = !!cur[k], has = !!prop[k];
    const name = k.toUpperCase() + (b27 ? (k === "sco" ? " " + pct(prop.level) + "–" + pct(b27.scoTop) + "%" : " " + pct(b27.ecoBottom) + "–" + pct(b27.ecoTop) + "%") : "");
    if (!had && has) { if (!isNum(pp[k])) return null; parts.push({ label: "add " + name, amount: round2(pp[k]) }); }
    else if (had && !has) { if (!isNum(at[k])) return null; parts.push({ label: "drop " + k.toUpperCase(), amount: round2(-at[k]) }); }
    else if (had && has) {
      if (!isNum(pp[k]) || !isNum(at[k])) return null;
      const d = round2(pp[k] - at[k]);
      if (Math.abs(d) >= 0.005) parts.push({ label: name, amount: d });
    }
  }
  const sum = parts.reduce((a, p) => a + p.amount, 0);
  if (Math.abs(sum - (Number(pp.total) - Number(at.total))) > 0.02) return null;
  return parts;
}

export function comparisonFor(u) {
  const cur = (u && u.current) || {};
  const prop = (u && u.proposed) || {};
  const at = cur.at2027 || null;
  const b27 = u && u.bands2027;
  const unitKnown = premiumBasisOf(u).ok;
  const curPts = areaPoints(cur.level, { sco: !!cur.sco, eco: !!cur.eco }, b27);
  const propPts = areaPoints(prop.level, { sco: !!prop.sco, eco: !!prop.eco }, b27);
  // No unit structure on file: the engine priced a structure nobody stated, so neither
  // premium is a figure for this farm.
  const pPrem = unitKnown && prop.premium ? num(prop.premium.total) : null;
  const cPrem = unitKnown && at ? num(at.total) : null;
  const pG = num(prop.guaranteeBu), cG = at ? num(at.guaranteeBu) : null;
  const pCov = prop.coverage ? num(prop.coverage.mpci) : null, cCov = at ? num(at.coverageMpci) : null;
  const parts = unitKnown ? changeParts(u) : null;
  const split = parts && parts.length > 1 ? { parts } : null;
  return {
    current: { level: pct(cur.level), sco: !!cur.sco, eco: !!cur.eco, scoPct: num(cur.scoPct), ecoPct: num(cur.ecoPct),
               premium: cPrem, guaranteeBu: cG,
               coverageMpci: cCov, areaPoints: curPts, unit: cur.unit || null },
    // BOTH SIDES CARRY THE SHARE. Putting it on the current column alone was the same defect
    // one column over: page two then read "ECO 90–95% at 50%" beside a proposal described as
    // the whole band, which is the comparison contradicting itself.
    proposed: { level: pct(prop.level), sco: !!prop.sco, eco: !!prop.eco, scoPct: num(prop.scoPct), ecoPct: num(prop.ecoPct),
                premium: pPrem, guaranteeBu: pG,
                coverageMpci: pCov, areaPoints: propPts, unit: prop.unit || null },
    diff: {
      premium: isNum(pPrem) && isNum(cPrem) ? round2(pPrem - cPrem) : null,
      guaranteeBu: isNum(pG) && isNum(cG) ? Math.round((pG - cG) * 10) / 10 : null,
      coverageMpci: isNum(pCov) && isNum(cCov) ? round2(pCov - cCov) : null,
      areaPoints: propPts.total - curPts.total,
      split,
    },
    // What the grower actually paid last year. A DIFFERENT crop year, so it is
    // carried with its year and never subtracted from a 2027 figure.
    billedPrior: cur.billedPrior && isNum(cur.billedPrior.total)
      ? { year: u.priorYear, total: num(cur.billedPrior.total) } : { year: u.priorYear, total: null },
    // ── LAST YEAR AS ITS OWN COLUMN, AND THE CROSS-YEAR CHANGE ────────────────
    // Kevin, 2026-09-22: page one is "LAST YEAR -> PROPOSED THIS YEAR -> CHANGE",
    // and the change he named is $24.75 − $17.13 = +$7.62.
    //
    // ⚠ THAT SUBTRACTION CROSSES CROP YEARS, which the `billedPrior` rule above
    // exists to prevent — last year's figure is a BILL at last year's rates and
    // prices, this year's is an ESTIMATE at this year's. It is what a grower
    // actually wants to know, so it is computed and it is LABELLED, never
    // presented as the coverage decision's own cost. `diff` above stays the
    // same-year comparison (both columns at this year's rates), which is the
    // only one that isolates the coverage change, and page two keeps it.
    // ⚠ A CROP THAT WAS NOT PLANTED HAS NO "LAST YEAR". Ontario's card printed
    // "75% RP + SCO + ECO · $350.21/ac" under LAST YEAR beside its own header
    // saying no wheat was planted in 2026 — coverage derived from RMA's published
    // price for a crop that did not exist. `notPlanted` empties the column, and
    // the CHANGE column with it: there is nothing to subtract from.
    lastYear: (u.priorPlanted === false
      ? { year: u.priorYear, notPlanted: true, level: null, sco: false, eco: false,
          scoPct: null, ecoPct: null, guaranteeBu: null, coverageTotal: null,
          coverageDerived: false, premium: null }
      : {
      year: u.priorYear, notPlanted: false,
      level: pct(cur.level), sco: !!cur.sco, eco: !!cur.eco,
      scoPct: num(cur.scoPct), ecoPct: num(cur.ecoPct),
      guaranteeBu: num(cur.priorGuaranteeBu),
      coverageTotal: sumLayers(cur.priorCoverage),
      coverageDerived: !!(cur.priorCoverage && cur.priorCoverage.derived),
      premium: cur.billedPrior && isNum(cur.billedPrior.total) ? num(cur.billedPrior.total) : null,
    }),
    proposedTotal: {
      coverageTotal: sumLayers(prop.coverage),
      guaranteeBu: pG,
      premium: pPrem,
    },
    vsLastYear: (() => {
      if (u.priorPlanted === false) return { crossYear: true, notPlanted: true, guaranteeBu: null, coverageTotal: null, premium: null };
      const lyG = num(cur.priorGuaranteeBu), lyC = sumLayers(cur.priorCoverage);
      const lyP = cur.billedPrior && isNum(cur.billedPrior.total) ? num(cur.billedPrior.total) : null;
      const pC = sumLayers(prop.coverage);
      return {
        crossYear: true,
        guaranteeBu: isNum(pG) && isNum(lyG) ? Math.round((pG - lyG) * 10) / 10 : null,
        coverageTotal: isNum(pC) && isNum(lyC) ? round2(pC - lyC) : null,
        premium: isNum(pPrem) && isNum(lyP) ? round2(pPrem - lyP) : null,
      };
    })(),
    priorNote: priorNote(u),
    aphNote: aphNote(u),
  };
}

// ⚠ LAST YEAR'S PREMIUM IS REPORTED WITH WHAT IT WAS FOR, OR WHY THERE IS NONE. "Your
// 2026 premium for this county is not in the insurance company's file" was false for DU
// MOND AG LLC's Seneca and Wayne wheat: the AIP's 2026 acreage record for each says no
// wheat was planted (code Z), so there was no premium to be missing. A 2026 bill also
// carried 2026's coverage (ECO 86–95%, the prevent-plant buy-up), so it differs from a
// 2027 figure by more than the price.
export function priorNote(u) {
  const py = u && u.priorYear;
  const t = u && u.current && u.current.billedPrior ? num(u.current.billedPrior.total) : null;
  if (isNum(t)) return "For reference, your " + py + " premium was " + money(t, { perAcre: true }) + ", for " + py + " coverage at " + py + " rates and prices.";
  if (u && u.priorPlanted === false) return "No wheat was planted in " + titleCounty(u.county) + " County in " + py + ", so there was no " + py + " premium.";
  return "No " + py + " premium for this county is on record.";
}
// The guarantee rests on an APH from a named year; where that is not the crop year being
// quoted, the page says so rather than presenting last year's APH as this year's.
export function aphNote(u) {
  const ay = aphYearOf(u);
  if (!u || !isNum(u.aph) || !isNum(ay.year) || !isNum(u.cropYear) || ay.mixed || Number(ay.year) >= Number(u.cropYear)) return null;
  const a = Number(u.aph);
  return "Guarantees use your " + ay.year + " APH of " + (Number.isInteger(a) ? a : a.toFixed(1)) + " bu; your " + u.cropYear + " APH is not on file yet.";
}

// ⚠ WHICH YEAR'S APPROVED YIELD THE GUARANTEE RESTS ON — ONE RULE, BOTH PAGES.
// `aphYear` is the year of the COVERAGE ROW the APH came from. Where the quote blended
// several APH databases (resolveUnitAph), that row can be the new crop year's while every
// database in the blend still stands on LAST year's approved yield — CLARK FIVE's Huntingdon,
// 2026-09-18: a 2027 row, a 98.7 blend of three databases, all three on 2026 yields. Page one
// printed "2027 approved yield: 98.7" over that. So:
//   every contributing database on last year's yield  -> last year's
//   some on last year's, some on this year's          -> MIXED: no single year is claimed
//   otherwise                                         -> the row's year
export function aphYearOf(u) {
  const b = u && u.aphBasis;
  const n = b ? num(b.databases) : null, np = b ? num(b.priorYieldDatabases) : null;
  const cy = u ? num(u.cropYear) : null;
  if (n != null && n > 0 && np != null && cy != null) {
    if (np >= n) return { year: cy - 1, mixed: false };
    if (np > 0) return { year: null, mixed: true };
  }
  return { year: u ? num(u.aphYear) : null, mixed: false };
}

// ── THE GUARANTEE ON PAGE ONE (work order D, 2026-09-18) ─────────────────────
//
// What a grower actually buys is bushels and dollars per acre, and page one printed
// neither — the guarantee was only on page two. So each county card now carries
//
//     Production guarantee   71.3 → 76.0 bu/ac
//     Revenue guarantee      $537.60 → $573.04/ac  at $7.54 projected price
//     Approved yield         2026: 95.0 bu · 2027 pending
//
// ⚠ EVERY FIGURE HERE IS ONE THE QUOTE PAGE PRINTED. The bushels are the quote's own
// `guarBu` and the dollars its `liabPerAc` — through resolveUnitAph and quoteRP, the SAME
// path the printed quote takes, so a multi-database unit is quoted per database and summed
// exactly as the quote is (Clark Five, 2026-09-18). This module does not multiply an APH by
// a coverage level: a second derivation is how the report and the quote came to disagree
// about Clark Five's APH in the first place.
//
// ⚠ THE APH SAYS WHICH YEAR IT IS. Before the AIP issues the new crop year's approved
// yield the quote runs on last year's, and "Approved yield 95.0" alone reads as a 2027
// figure. Where no APH resolved at all it is "Pending" and so is every guarantee beside it.
const bu1 = (v) => (isNum(v) ? Number(v).toFixed(1) : null);
export function guaranteeFor(u, cmp) {
  const cy = u ? num(u.cropYear) : null;
  const aph = u ? num(u.aph) : null;
  const ay = aphYearOf(u);
  const aphYear = ay.year;
  const priorYearAph = aph != null && aphYear != null && cy != null && aphYear < cy;
  const basis = u && u.aphBasis && typeof u.aphBasis.note === "string" && u.aphBasis.note ? u.aphBasis.note : null;
  const aphLine = aph == null
    ? "Approved yield — Pending"
    : priorYearAph
      ? aphYear + " approved yield: " + bu1(aph) + " bu · " + cy + " pending"
      : ay.mixed
        ? "Approved yield: " + bu1(aph) + " bu, " + (cy != null ? (cy - 1) + " and " + cy + " yields" : "two years' yields")
        : (aphYear != null ? aphYear + " approved yield: " : "Approved yield: ") + bu1(aph) + " bu";
  const side = (s) => ({ bu: aph == null ? null : num(s.guaranteeBu), revenue: aph == null ? null : num(s.coverageMpci) });
  const price = u && u.price ? num(u.price.value) : null;
  // No current coverage (new business): there is no "from", and a Pending on that side
  // would read as a figure we failed to find rather than one that does not exist.
  const isNew = !(u && u.current && isNum(u.current.level));
  return {
    isNew,
    current: !isNew && cmp && cmp.current ? side(cmp.current) : { bu: null, revenue: null },
    proposed: cmp && cmp.proposed ? side(cmp.proposed) : { bu: null, revenue: null },
    price,
    // ⚠ CV AND COUNTY BASIS RIDE ON `aph` SO THE MAIN REPORT CAN SHOW THEM. They were
    // emitted into the fact sink and rendered only by technicalNotesHtml — the APPENDIX —
    // so a Full Strategy Report printed without the appendix showed neither, which is
    // what Kevin saw. They belong beside the approved yield, because that is the number
    // they qualify: CV says how steady this farm's own yield is, basis says how it sits
    // against the county the SCO/ECO trigger settles on.
    //
    // NAMED `cvPct` / `countyExpBu` / `yieldBasis` deliberately — the same keys the fact
    // sink uses — so `yieldContextLine` reads this object unchanged and there is ONE
    // rule, not a second copy that drifts. Values are READ, never re-derived here.
    aph: { value: aph, year: aphYear, priorYear: priorYearAph, pending: aph == null, line: aphLine, basis,
           cvPct: u && u.cvPct != null ? u.cvPct : null,
           countyExpBu: u && u.countyExpBu != null ? u.countyExpBu : null,
           yieldBasis: u && u.yieldBasis != null ? u.yieldBasis : null },
  };
}

// ── 5. WHAT WE STILL NEED — ONE REQUEST PER THING THE GROWER DOES ────────────
//
// ⚠ DEDUPED BY WHAT THE GROWER IS BEING ASKED TO DO, NOT BY TEXT. V1 raised the
// acreage request once per county block, and three counties with three different
// acreages printed three different sentences — so a text dedupe correctly kept all
// three, and the farmer was asked for the same thing three times. It is ONE request
// ("tell us your 2027 wheat acres by county") with the county figures underneath.
// ⚠ ACREAGE IS NO LONGER ASKED FOR, AND THE KEY STAYS SO THE ABSENCE IS ASSERTABLE.
// Kevin, 2026-09-17: "I would remove that we need their acres — we obviously need their
// acres but we will get them via FSA-578 or via precision machine data. They will not
// have finalized acres yet." Asking a grower in September for acres he cannot have, on a
// document whose whole point is that it only says what it can source, reads as a request
// he has to act on. The per-county figure each premium rests on is still stated on the
// unit line (acreageText) and page one still says premiums are estimates until the acres
// are reported — what is gone is the REQUEST. tools/check-strategy-report.js asserts
// nothing emits this kind, so restoring the block fails rather than quietly reappearing.
export const NEED = { ACREAGE: "acreage", UNIT: "unit-structure", FSA_YIELD: "fsa-payment-yield", ORGANIC_PRICE: "organic-price" };
// `u` (optional) lets the line say what the acres rest on for THIS county: a placeholder
// acreage behind a premium the report does not quote is not stated at all, and prior-year
// acres from a year before last say when wheat was last planted.
export function acreageScopeText(a, cropYear, u) {
  if (!a || !isNum(a.acres)) return "no acres on file";
  const ac = Math.round(Number(a.acres)).toLocaleString("en-US") + " acres";
  // Kevin, 2026-09-17: "we never assume, remove that wording". The figure a premium was
  // priced on is still stated — with where it comes from — and never called assumed.
  if (a.source === ENGINE.ACRE.PRIOR_YEAR) {
    const y = a.year || cropYear - 1;
    const gap = u && u.priorPlanted === false && Number(y) < cropYear - 1 ? "; no wheat was planted in " + (cropYear - 1) : "";
    return ac + ", from your " + y + " acreage" + gap;
  }
  if (a.source === ENGINE.ACRE.ASSUMED) return "no wheat acres on record — the premium is pending";
  if (a.source === ENGINE.ACRE.PLANNED) return ac + " — planned, not yet reported";
  return ac + " reported";
}
// WHERE THE ACRES CAME FROM — the basis, never a request. Removing the acreage ask (above)
// must not remove the statement of what a premium total was figured on: "about $11,980 on
// 350 ac" with no source is exactly the kind of figure this report exists not to print. It
// rides on the county line beside the acres, so the two cannot drift apart.
export function acreageSourceText(a, cropYear, u) {
  if (!a || !isNum(a.acres)) return null;
  if (a.source === ENGINE.ACRE.PRIOR_YEAR) {
    const y = a.year || cropYear - 1;
    // A gap year is the reason those acres are the newest we hold, so it is said here too.
    const gap = u && u.priorPlanted === false && Number(y) < cropYear - 1 ? "; none planted in " + (cropYear - 1) : "";
    return "your " + y + " acreage" + gap;
  }
  if (a.source === ENGINE.ACRE.ASSUMED) return "no acres on record";
  if (a.source === ENGINE.ACRE.PLANNED) return "planned, not yet reported";
  return "reported";
}
export function synthesizeNeeds({ units, fsa, cropYear }) {
  const needs = [];
  // No acreage request: see the note on NEED.ACREAGE. The acres reach us from the
  // FSA-578 or from machine data, and in September the grower does not have final ones.
  const noUnit = (units || []).filter((u) => !u.priceMissing && !unitInfo(u).known);
  if (noUnit.length) {
    needs.push({ kind: NEED.UNIT, title: "Your unit structure by county",
      body: "Premium depends on it (basic, optional or enterprise units). Until we have it, these counties are not quoted:",
      scopes: noUnit.map((u) => ({ label: titleCounty(u.county) + (u.practiceLabel ? " · " + u.practiceLabel : ""), detail: "not on file" })) });
  }
  // ⚠ ASKED FOR WHERE IT DECIDES SOMETHING, AND NOTHING IS ASSUMED MEANWHILE (#208 + work
  // order E, 2026-09-18). The payment yield decides the ENROLMENT wherever electionOutlook
  // has a switch yield (above it PLC pays at least as much at every price), and it decides
  // the forecast comparison where both programs pay (the threshold state). #208 printed
  // "Until then we use 60 bu/acre" beside this; nothing is used until then now.
  const known = fsa && isNum(fsa.paymentYield) && Number(fsa.paymentYield) > 0;
  const el = fsa && !known ? (fsaSection(fsa) || {}).election : null;
  const swRows = el ? el.rows.filter((r) => isNum(r.switchYield)) : [];
  const cond = fsa && !known ? (fsa.rows || []).filter((r) => r.read && r.read.state === "threshold" && isNum(r.read.breakeven)) : [];
  if (swRows.length || cond.length) {
    // ⚠ IT NO LONGER POINTS AT A BOX BELOW IT (2026-09-19). The report's own FSA narrative was
    // removed — the election is stated once, on the farm election sheet at the end of the
    // document — so this ask names the SHEET rather than a block that is no longer there.
    // With one county it still states the rule outright, because that costs no more words.
    const decides = swRows.length === 1 && swRows[0].counties.length && (el.rows.length === 1)
      ? "Above " + swRows[0].switchYield + " bu we recommend PLC; below it, " + BELOW_SWITCH_ARC
      // ⚠ THE PAYMENT YIELD SETS THE LINE; THE PRICE DECIDES THE SIDE. This said it
      // "decides which program each county reads", which hands one input the whole
      // outcome — and the election sheet three pages later states the rule correctly
      // ("ARC-CO unless the price ends below $X; PLC if it does · your FSA payment
      // yield sets the line"). Two pages of one document describing the same
      // determination differently, with the shorter one overstating. It is the same
      // class as the collapsed "below the switch yield -> ARC-CO" wording already
      // retired from every other surface.
      : swRows.length ? "It sets the price line at which PLC overtakes ARC-CO on the election sheet at the end of this report"
      : "It decides ARC-CO versus PLC where both can pay";
    needs.push({ kind: NEED.FSA_YIELD, title: "Your FSA wheat payment yield",
      body: "It is on your FSA farm record — it is not your APH. " + decides + ".",
      scopes: [] });
  }
  const orgPending = (units || []).filter((u) => u.priceMissing);
  if (orgPending.length) {
    needs.push({ kind: NEED.ORGANIC_PRICE, title: "RMA's " + cropYear + " organic wheat price",
      body: "Organic wheat cannot be priced until RMA announces its " + cropYear + " organic price election.",
      scopes: orgPending.map((u) => ({ label: titleCounty(u.county), detail: "priced when announced" })) });
  }
  // One entry per kind, whatever produced it.
  const seen = new Set();
  return needs.filter((x) => (seen.has(x.kind) ? false : (seen.add(x.kind), true)));
}

// ── IMPORTANT FOR THIS CROP YEAR ─────────────────────────────────────────────
// ⚠ THE PRICE'S STATUS IS SUPPLIED, NEVER DERIVED HERE. What a reader is told about
// a resolved RMA price is one rule in App.jsx (the price ladder's display); a second
// copy here would drift. This only refuses a label that contradicts its own tone.
export const PRICE_TONE = { FINAL: "final", SETTLED: "settled", ESTIMATE: "estimate", PENDING: "pending", CONFLICT: "conflict" };
export function importantFor(units, price, cropYear) {
  const out = [];
  if (price && isNum(price.value))
    out.push({ key: "price", label: "Projected wheat price", value: money(price.value) + "/bu",
               note: (price.display && price.display.label) || null, detail: (price.display && price.display.detail) || null,
               tone: price.display ? price.display.tone : null });
  else out.push({ key: "price", label: "Projected wheat price", value: PENDING, note: "not yet published", tone: PRICE_TONE.PENDING });

  const pp = (units || []).map((u) => u.pp).filter((p) => p && isNum(p.factor));
  if (pp.length) {
    const f = [...new Set(pp.map((p) => pct(p.factor)))];
    const lost = (units || []).filter((u) => u.pp && u.pp.buyUpHeldPrior && u.pp.buyUpOfferedNow === false && isNum(u.pp.factorPrior));
    // ⚠ A FARM-WIDE SENTENCE ABOUT SOME COUNTIES IS FALSE ABOUT THE OTHERS. Where only some
    // units carried the buy-up, the note names them.
    const priced = (units || []).filter((u) => u.pp && isNum(u.pp.factor));
    const where = lost.length && lost.length < priced.length ? " in " + lost.map((u) => titleCounty(u.county)).join(" and ") + (lost.length > 1 ? " Counties" : " County") : "";
    out.push({ key: "pp", label: "Prevented planting",
      value: f.length === 1 ? f[0] + "% of your guarantee" : "varies by county",
      note: lost.length ? "The " + pct(lost[0].pp.factorPrior) + "% buy-up you carried" + where + " in " + (cropYear - 1) + " is not offered for " + cropYear : null });
  }
  const anyArea = (units || []).some((u) => (u.proposed && (u.proposed.sco || u.proposed.eco)) || (u.current && (u.current.sco || u.current.eco)));
  const b = (units || []).map((u) => ({ a: u.bands2026, c: u.bands2027 })).find((x) => x.a && x.c);
  // What SCO and ECO ARE, said once for the farm rather than on every card that adds them —
  // a card that says "Add SCO" and explains only the base level leaves the reader asking why.
  // ⚠ "AT LEAST 80%": the 2027 engine's SCO/ECO subsidy is 0.80 (SCO_ECO_SUBSIDY), and a
  // beginning farmer's is higher, so a flat "80%" would understate it for them.
  const anyEco = (units || []).some((u) => u.proposed && u.proposed.eco);
  if (anyArea && b) {
    const reach = pct(anyEco ? b.c.ecoTop : b.c.scoTop);
    const changed = !same(b.a.scoTop, b.c.scoTop)
      ? " " + cropYear + ": SCO to " + pct(b.c.scoTop) + "%, ECO " + pct(b.c.ecoBottom) + "–" + pct(b.c.ecoTop) + "% (" + pct(b.a.ecoBottom) + "–" + pct(b.a.ecoTop) + "% in " + (cropYear - 1) + ")."
      : "";
    // Why they are in the recommendation, said plainly: they are included wherever the county
    // offers them (the engine's standing structure), not because this farm's history proved them.
    out.push({ key: "area-bands", label: "SCO and ECO",
      value: "County-based coverage above your own, up to " + reach + "%",
      note: "Included wherever offered; the government pays at least 80% of the premium." + changed });
  }
  // ⚠ WHAT MAKES A PREMIUM AN ESTIMATE IS EVERY INPUT THAT IS NOT FINAL, NOT ONLY ACRES.
  // Beside "Final RMA projected price" the old line named acreage alone, while every premium
  // also rested on a price volatility factor RMA had not published into our data (0.27, the
  // carrier's own 2027 estimate).
  const acresOpen = (units || []).some((u) => u.acreage && u.acreage.source !== ENGINE.ACRE.REPORTED);
  // "rma" is a feed row; "released" is RMA's released CY2027 figure, cited (WHEAT_2027_VOLATILITY_RELEASE).
  const volEst = (units || []).map((u) => u.vol).filter((v) => v && isNum(v.value) && v.status !== "rma" && v.status !== "released");
  const vols = [...new Set(volEst.map((v) => Number(v.value).toFixed(2)))];
  if (acresOpen || volEst.length) {
    const value = volEst.length && acresOpen ? "until RMA's " + cropYear + " volatility factor and your acres are in"
      : volEst.length ? "until RMA's " + cropYear + " volatility factor is in"
      : "until your " + cropYear + " acres are reported";
    out.push({ key: "acreage", label: "Premiums are estimates", value,
      note: volEst.length ? "Priced with an estimated price volatility factor of " + (vols.length === 1 ? vols[0] : vols.join(" / ")) : null });
  }
  return out;
}

// ── FSA AND MARKET — never part of the crop-insurance recommendation ─────────
// ⚠ THE COLUMN IS "HIGHER ESTIMATED PAYMENT", NOT "CURRENT LEAN". Kevin, 2026-09-17, on
// Dumond's table: "is this saying that dumond currently has PLC elected? How would we know
// that?" We would not — no source we hold carries a farm's ARC/PLC election. And Seneca read
// PLC beside ARC-CO $18.10 > PLC $17.85, because the rule calls a gap inside its tie band
// for PLC. A gap inside that band is printed as "About even"; outside it, the program the
// rule picks — which is then always the higher figure.
export const FSA_EVEN = "About even";
// Two answers that are NOT a winner: one because nothing pays, one because the thing that
// would decide it is a farm figure Silas does not hold.
const FSA_NONE = "Neither projected to pay";
// A county FSA splits by irrigation, with no irrigated share held for this farm: no figure.
export const FSA_SPLIT = "Needs your irrigated share";

// ⚠ THIS SECTION READS THE ENGINE; IT DECIDES NOTHING (work order E, 2026-09-18).
// It used to take rows priced at a PLC payment yield the caller hard-coded (60 bu), work
// out its own crossover (arc / (rate x 0.85)) and name a winner off the placeholder. Each
// row now carries countyRead's READ — the same call the Coverage Advisor and the one-page
// election sheet make — and the label is electionLabel's, so three surfaces word one
// answer one way. What county data decides without the farm is stated as decided:
//
//   neither pays                 -> "Neither projected to pay", at ANY payment yield
//   ARC-CO pays, PLC cannot      -> ARC-CO, at ANY payment yield
//   PLC pays, ARC-CO cannot      -> PLC, at any payment yield above zero
//   both pay                     -> "PLC above N bu payment yield, ARC-CO below" — a
//                                   CONDITION, because the farm figure that decides it
//                                   is one Silas does not hold (BUG-090)
//
// With a KNOWN payment yield (fsa.paymentYield, from a caller that has the farm's own
// figure) the read is "decided" and the tie band applies: a gap inside it is "About even".
export function fsaSection(fsa) {
  // PENDING: the active program year (cropYear.js electionYearContext) is one FSA has not
  // published figures for. The report says so in the caller's words and prints no county
  // figure — never another year's in its place.
  if (fsa && fsa.pending && !(fsa.rows || []).length)
    return { pending: String(fsa.pending), programYear: fsa.programYear || null, heading: "FSA program election", rows: [] };
  if (!fsa || !(fsa.rows || []).length) return null;
  const b = fsa.basis || null;
  const tie = b && isNum(b.tieBand) ? Number(b.tieBand) : 0.5;
  const knownYield = isNum(fsa.paymentYield) && Number(fsa.paymentYield) > 0;
  const rows = fsa.rows.map((r) => {
    const rd = r.read || null;
    const out = { county: titleCounty(r.county), state: r.state || null, readState: rd ? rd.state : null,
      arc: rd ? num(rd.arcPerAcre) : null, plcRate: rd ? num(rd.plcRate) : null,
      plc: rd ? num(rd.plcPerAcre) : null,
      breakeven: rd && rd.state === "threshold" && isNum(rd.breakeven) ? Math.round(Number(rd.breakeven)) : null };
    if (r.refused) return { ...out, lean: FSA_SPLIT, refused: r.refused };
    let lean = electionLabel(rd);
    if (rd && rd.state === "decided" && isNum(out.plc) && isNum(out.arc) && Math.abs(out.arc - out.plc) <= tie + EPS) lean = FSA_EVEN;
    return { ...out, lean };
  });
  const conditional = rows.filter((r) => r.readState === "threshold");
  // Preliminary while ANY input is still an estimate: the marketing-year price, the county
  // yield, or a payment yield that would decide a county.
  const preliminary = !knownYield || (b && b.status !== "final") || (b && b.countyYieldsFinal === false) || conditional.length > 0;
  const py = fsa.programYear ? fsa.programYear + " " : "";
  const basisBits = [];
  if (b && isNum(b.price)) basisBits.push(b.status === "final" ? "the final " + py + "wheat price of " + money(b.price)
                                                                : "USDA's " + money(b.price) + " forecast " + py + "wheat price");
  if (b && b.countyYieldsFinal === false) basisBits.push("each county's historical average yield (" + (fsa.programYear || "") + " county yields are not published yet)");
  // The farm's OWN payment yield where a caller holds one — never a placeholder. #208 fed
  // electionOutlook a 60 bu stand-in to pick a default side; without one its lean is null
  // and the recommendation is the condition itself (reportV2Html electionHtml).
  const stated = knownYield ? Number(fsa.paymentYield) : null;
  // ── WHICH ONE DO I ENROL IN? ───────────────────────────────────────────────
  // Kevin, 2026-09-18, on a page that read "Neither projected to pay" twice: *"it
  // doesnt give us any actionable steps … I still need to enroll in one, which one
  // should I enroll in and why?"* Both halves are right. the forecast comparison above answers
  // the election for a year whose price is KNOWN — it compares two payments,
  // so when neither pays it has nothing to compare, and the page fell silent
  // on a decision the grower still has to make by FSA's deadline.
  //
  // What can be said in advance is where each one STARTS, and both thresholds are
  // published. Measured on this book 2026-09-18: NY wheat 2026 forecasts a $6.40 MYA
  // against a $6.35 reference price and a Livingston guarantee of $529.57/ac — PLC is
  // five cents from paying and ARC-CO is twelve, so "neither pays" was hiding a
  // decision that turns on a 1% move. The arithmetic is electionOutlook's; this only
  // puts it into sentences.
  const refPrice = b && isNum(b.refPrice) ? Number(b.refPrice) : null;
  const mya = b && isNum(b.price) ? Number(b.price) : null;
  const outlookOf = (r) => electionOutlook({
    effectiveRefPrice: refPrice, myaPrice: mya, paymentYield: stated,
    benchmarkYield: r.benchmarkYield, benchmarkRevenue: r.benchmarkRevenue,
    guaranteeRevenue: r.guaranteeRevenue,
    loanRate: b && isNum(b.loanRate) ? Number(b.loanRate) : null,
  });
  const outlooks = fsa.rows.map((r) => ({ county: titleCounty(r.county), o: outlookOf(r) }))
    .filter((x) => x.o && x.o.refused == null && isNum(x.o.arcTriggerPrice) && isNum(x.o.plcTriggerPrice));
  // ⚠ WHAT IS NATIONAL IS SAID ONCE; WHAT IS THE COUNTY'S IS SAID PER COUNTY.
  // The ARC-CO benchmark PRICE is national — measured 2026-09-18 it is $6.98 in all five
  // NY wheat counties this book writes — and so is the reference price, so both PRICE
  // triggers ($6.35 PLC, $6.28 ARC-CO) and the price at which ARC-CO caps are the same
  // everywhere. What differs is the county's own benchmark YIELD, and the switch payment
  // yield scales with it: Cayuga 78, Seneca 70, Wayne 68 bu. A block per county would have
  // printed Du Mond's page with the same three sentences three times.
  //
  // Counties are grouped by what would be PRINTED, so two whose figures round to the same
  // text share one line (Livingston and Monroe differ by seven hundredths of a bushel).
  const pct = (a, of_) => (isNum(a) && isNum(of_) && of_ > 0 ? Math.abs(a / of_) * 100 : null);
  const perCounty = outlooks.map((x) => {
    const o = x.o;
    const bY = isNum(o.arcTriggerYield) && isNum(o.yieldFallToArc) ? o.arcTriggerYield + o.yieldFallToArc : null;
    return {
      county: x.county, o,
      lean: o.lean,
      arcYield: isNum(o.arcTriggerYield) ? o.arcTriggerYield.toFixed(1) : null,
      arcYieldFall: pct(o.yieldFallToArc, bY) != null ? pct(o.yieldFallToArc, bY).toFixed(1) : null,
      // Whole dollars: the cap is 12% of each county's own benchmark revenue, so two
      // counties a few cents apart print one figure rather than two near-identical ones.
      capExact: isNum(o.arcCapPerBaseAcre) ? o.arcCapPerBaseAcre : null,
      cap: isNum(o.arcCapPerBaseAcre) ? Math.round(o.arcCapPerBaseAcre) : null,
      switchYield: isNum(o.switchYield) ? Math.round(o.switchYield) : null,
      switchReason: o.switchReason || null,
      arcWinsBetween: o.arcWinsBetween && o.arcWinsBetween.every(isNum)
        ? [money(o.arcWinsBetween[0]), money(o.arcWinsBetween[1])] : null,
    };
  });
  const printedKey = (c) => [c.lean, c.switchYield, c.arcYield, c.arcYieldFall, c.cap,
                             (c.arcWinsBetween || []).join("-"), c.switchReason].join("|");
  const rowsOut = [];
  for (const c of perCounty) {
    const g = rowsOut.find((r) => r.key === printedKey(c));
    if (g) g.counties.push(c.county); else rowsOut.push({ ...c, key: printedKey(c), counties: [c.county] });
  }
  // The price triggers are only printed ONCE where they genuinely are the same to the cent;
  // otherwise the shared line is withheld rather than borrowed from the first county.
  const sameCent = (f) => outlooks.every((x) => isNum(f(x.o)) && Math.round(f(x.o) * 100) === Math.round(f(outlooks[0].o) * 100));
  const sharedPrices = outlooks.length > 0 && sameCent((o) => o.plcTriggerPrice) && sameCent((o) => o.arcTriggerPrice) && sameCent((o) => o.capPrice);
  const o0 = outlooks.length ? outlooks[0].o : null;
  const leans = [...new Set(rowsOut.map((r) => r.lean))];
  const election = !rowsOut.length ? null : {
    lean: leans.length === 1 ? leans[0] : null,
    sharedPrices,
    single: rowsOut.length === 1,
    // Each program's own trigger, in the units the grower reads it in. Percentages sit in
    // parentheses: the first render chained four em-dashes into one line and read as a
    // run-on. "Average yield" matches the basis note, which calls the benchmark each
    // county's historical average yield.
    plcTrigger: sharedPrices ? "the season-average price ends below " + money(o0.plcTriggerPrice)
      + (pct(o0.priceFallToPlc, mya) != null ? " (a " + pct(o0.priceFallToPlc, mya).toFixed(1) + "% fall from USDA's " + money(mya) + " forecast)" : "") : null,
    arcAsPrice: sharedPrices ? "at the county's average yield, a price under " + money(o0.arcTriggerPrice)
      + (pct(o0.priceFallToArc, mya) != null ? " (a " + pct(o0.priceFallToArc, mya).toFixed(1) + "% fall)" : "") : null,
    forecast: mya != null ? money(mya) : null,
    // The two asymmetries. Neither needs a payment yield, and neither is a forecast.
    onlyArc: "Only ARC-CO pays on a short crop in your county. PLC never looks at your county's yield.",
    onlyPlc: sharedPrices && isNum(o0.capPrice)
      // One county names its cap in dollars; several say where ARC-CO tops out, because
      // the dollars differ by county and the price at which it happens does not.
      ? (rowsOut.length === 1 && rowsOut[0].cap != null
        ? "Only PLC keeps paying in a collapse: ARC-CO caps at " + (rowsOut[0].counties.length === 1 ? money(rowsOut[0].capExact) : "$" + rowsOut[0].cap)
          + " per base acre, reached at " + money(o0.capPrice) + "; PLC has no cap."
        : "Only PLC keeps paying in a collapse: ARC-CO maxes out once the price reaches " + money(o0.capPrice) + "; PLC has no cap.") : null,
    // A cap is printed to the CENT for one county, and to the dollar only where counties
    // a few cents apart were merged into one line — "$60.00" would claim a precision the
    // merged figure does not have (Livingston $60.02, Monroe $59.97).
    // The one below-the-switch statement (arcPlcElection.js), so the renderer cannot word it stronger.
    belowSwitch: BELOW_SWITCH_ARC,
    rows: rowsOut.map(({ key, o, county, capExact, ...r }) => ({ ...r,
      cap: r.cap == null ? null : r.counties.length === 1 ? money(capExact) : "$" + r.cap })),
  };
  // ⚠ A TABLE WHOSE EVERY ROW SAYS THE SAME THING IS ONE SENTENCE. When no county is
  // projected to pay under either program, the forecast table printed "none / none /
  // Neither projected to pay" once per county — the part Kevin called unhelpful, and
  // the part that pushed a three-county farm onto a fifth sheet once the election
  // answer sat above it. It collapses ONLY when that answer is present to carry the
  // decision; a county that pays keeps the table, because then its figures are news.
  // The payment yield is not named in the sentence because it cannot matter here:
  // FSA_NONE requires a zero PLC rate, and a zero rate pays nothing at any yield.
  // Every county in the engine's neither-pays state — read off countyRead, not re-derived.
  const allNeither = rows.length > 0 && rows.every((r) => r.readState === "neither_pays");
  const cList = fsa.rows.map((r) => titleCounty(r.county));
  const listed = cList.length <= 1 ? cList.join("") : cList.slice(0, -1).join(", ") + " or " + cList[cList.length - 1];
  const neitherText = allNeither && election
    ? (mya != null ? "At USDA's " + money(mya) + " forecast" : "At USDA's current forecast")
      // Past two counties the list is the same counties the answer above just named.
      + ", neither program is projected to pay in " + (cList.length > 2 ? "any of these counties" : listed)
      + (b && b.countyYieldsFinal === false ? " — " + (fsa.programYear || "this year's") + " county yields are not published yet" : "")
      + "."
    : null;
  // ⚠ WHOSE COUNTY: the report's counties are the ones the wheat is INSURED in unless a caller hands
  // it an FSA base-acre record (none exists today) — a scenario, never the FSA farm election result.
  const countyBasis = fsa.countyBasis === "fsa" ? "fsa" : "insurance";
  return {
    programYear: fsa.programYear || null,
    countyBasis,
    countyBasisNote: countyBasis === "fsa" ? null
      : "Counties are the ones your wheat is insured in — an insurance-county scenario. Silas does not hold where your FSA farm's base acres are, so this is not your FSA farm election result.",
    preliminary,
    election, allNeither: !!neitherText, neitherText,
    paymentYield: stated,
    heading: preliminary ? "Preliminary FSA analysis" : "FSA program election",
    columnLabel: "Which pays more",
    tieBand: tie,
    rows,
    conditional: conditional.map((r) => ({ county: r.county, at: r.breakeven })),
    basis: basisBits.length ? "Estimates use " + basisBits.slice(0, -1).join(", ") + (basisBits.length > 1 ? " and " : "") + basisBits[basisBits.length - 1] + ". Per base acre, on wheat base acres only." : null,
    evenNote: "“About even”: within " + money(tie) + " per base acre.",
    // The one farm figure, named where it decides a county and nowhere else.
    yieldNote: conditional.length
      ? "Where a county reads “PLC larger above N bu”, compare N with the PLC payment yield on your FSA farm record — not your APH. That comparison is at USDA's forecast price only; at other prices it moves."
      : null,
    stillNeeded: conditional.length ? "your FSA wheat payment yield" : null,
  };
}
// ⚠ A REACH FIGURE IS A SHARE OF PAST DAYS, OF A MOVE THIS SIZE. It is measured on the
// nearby futures series over the last few years (grainFillCurve), as the percentage rise
// from the board — not the probability of this contract reaching this price. The board
// figure is a dated settle, not "today".
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function dateText(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? MONTHS[Number(m[2]) - 1] + " " + Number(m[3]) + ", " + m[1] : null;
}
export function marketSection(market) {
  if (!market || !isNum(market.current)) return null;
  const cur = num(market.current);
  // ⚠ THE WINDOW IS THE ONE THE FILL RATE WAS MEASURED OVER, AND THE BOARD HAS ITS OWN.
  // The curve holds fixed buckets (21/42/63/126/189/252 trading days) and answers with the
  // nearest one to the trading time this board has left, so the two can differ by a month.
  // Both are stated: a fill rate measured over nine months must not be presented as the
  // board's full remaining life.
  const months = isNum(market.reachTradingDays) ? Math.max(1, Math.round(Number(market.reachTradingDays) / 21)) : null;
  const boardMonths = isNum(market.boardTradingDays) ? Math.max(1, Math.round(Number(market.boardTradingDays) / 21)) : null;
  const asOf = dateText(market.asOf);
  return {
    heading: "Futures market context",
    subheading: "Reference levels only — not recommendations to sell",
    contract: market.contractLabel || null,
    current: cur,
    currentLabel: asOf ? "Latest settle, " + asOf : "Latest settle",
    levels: (market.levels || []).filter((l) => isNum(l.price)).map((l) => {
      const rise = cur > 0 ? Math.round((Number(l.price) / cur - 1) * 100) : null;
      const reach = isNum(l.reach) ? Math.round(Number(l.reach) * 100) : null;
      // Kevin, 2026-09-17: "'reached in 44% of days' is a little confusing — I would just
      // change that to that a futures target at that level would fill before the board
      // expires." That IS what the curve measures: from a past day, did the market trade at
      // least this much higher inside the window. So the row states the FILL, past tense,
      // and the note underneath states how it was measured.
      return { price: num(l.price), reach: isNum(l.reach) ? Number(l.reach) : null, risePct: rise,
        text: rise != null && reach != null
          ? "A " + rise + "% rise from the latest settle — an order there would have filled before this board expired " + reach + "% of the time"
          : "Reference level" };
    }),
    note: "How often: from every trading day" + (isNum(market.lookbackYears) ? " in the last " + market.lookbackYears + " years" : "") +
      " of nearby wheat futures, did the market trade at least that much higher" + (months ? " within " + months + " months" : "") +
      (months && boardMonths ? (boardMonths === months ? " — the trading time this board has left" : " — this board has about " + boardMonths + " months left") : "") +
      ". History, not a forecast, and not this contract's own record." +
      (market.projectedPriceContract ? " Your projected price comes from the " + market.projectedPriceContract.replace(/^(\w+) (\d{2})$/, (m0, mo, yy) => ({ Jan: "January", Feb: "February", Mar: "March", May: "May", Jul: "July", Sep: "September", Dec: "December" }[mo] || mo) + " 20" + yy) + " contract, not this one." : ""),
  };
}

// ── THE COUNTY TRIGGER RECORD, SUMMARISED ────────────────────────────────────
// `trigger` arrives already computed by the SAME function the detailed trigger page
// uses (wheatTriggerStats in App.jsx, over RMA's settled county revenue), at the
// level and endorsements this report recommends.
// ⚠ THE RECORD SAYS WHAT ITS DOLLARS ARE FIGURED ON, AND WHETHER IT PAID WHEN THIS FARM
// FELL SHORT. The dollars are today's bands at the farm's CURRENT APH and each year's own
// projected price — not what the grower was paid. And a county record that paid in 2019 and
// 2025 says nothing about a farm whose short years were 2017, 2018 and 2020 unless it is said:
// DU MOND AG LLC's Cayuga county coverage would not have paid in any of them.
function lossYearLine(u, t) {
  const f = u && u.engine && u.engine.areaFit;
  if (!f || !Array.isArray(f.lossYears) || !f.lossYears.length) return null;
  const inRecord = f.lossYears.filter((y) => (t.years || []).some((x) => Number(x.year) === Number(y))).sort((a, b) => a - b);
  if (!inRecord.length) return null;
  const paid = inRecord.filter((y) => { const x = t.years.find((z) => Number(z.year) === Number(y)); return x && Number(x.paid) > 0; });
  const lvl = isNum(f.lossLevel) ? pct(f.lossLevel) : null;
  const which = "In the " + (inRecord.length === 1 ? "year" : "years") + " your yield fell below " + (lvl != null ? lvl + "% of " : "") + "your APH (" + inRecord.join(", ") + ")";
  const name = t.showSco && t.showEco ? "SCO and ECO" : t.showSco ? "SCO" : "ECO";
  if (!paid.length) return which + ", " + name + " would not have paid.";
  if (paid.length === inRecord.length) return which + ", " + name + " would have paid in " + (paid.length === 1 ? "that year" : "each of them") + ".";
  return which + ", " + name + " would have paid only in " + paid.join(", ") + ".";
}
export function historyFor(u) {
  const t = u && u.trigger;
  if (!t || !isNum(t.n) || !t.n) return null;
  const parts = [];
  if (t.showSco) parts.push("SCO would have paid in " + t.scoYears + " of " + t.n + " years");
  if (t.showEco) parts.push("ECO in " + t.ecoYears + " of " + t.n + " years");
  const a = u && isNum(u.aph) ? Number(u.aph) : null;
  return {
    firstYear: t.firstYear, lastYear: t.lastYear, n: t.n,
    basis: "Dollars per acre are figured at your current APH" + (a != null ? " of " + (Number.isInteger(a) ? a : a.toFixed(1)) + " bu" : "") + " and each year's projected price.",
    lossLine: lossYearLine(u, t),
    sentence: parts.length ? cap(parts.join(", ")) + (isNum(t.avgAll) ? " — an average of " + money(t.avgAll, { perAcre: true }) + " a year." : ".") : null,
    worst: t.worst && isNum(t.worst.amount) && t.worst.amount > 0 ? { year: t.worst.year, amount: t.worst.amount } : null,
    years: (t.years || []).map((y) => ({ year: y.year, ratio: num(y.ratio), paid: num(y.paid) })),
    showSco: !!t.showSco, showEco: !!t.showEco, bandBottom: t.bandBottom, trigTop: t.trigTop,
  };
}

// ── THE WHOLE REPORT ─────────────────────────────────────────────────────────
export function synthesizeStrategyReport({ farm, crop = "WHEAT", cropYear, units = [], fsa = null, market = null, price = null, findings = [] }) {
  const cy = Number(cropYear);
  const recs = (units || []).map((u) => {
    const base = baseAction(u);
    const area = areaAction(u);
    const reasons = reasonsFor(u);
    const cmp = comparisonFor(u);
    const nothingChanges = base.kind === BASE_ACTION.KEEP && !area.changes;
    const pb = premiumBasisOf(u);
    const unitKnown = pb.ok;
    // ⚠ A DERIVED UNIT STRUCTURE IS NOT A CUSTOMER FACT, SO IT MAY NOT PRODUCE AN
    // INSTRUCTION. Every premium figure below is already withheld when the unit
    // structure is not on file, and `reasons.why` already says "N% is not a firm
    // recommendation yet" — but `status` and `base.text` went on reading "Keep
    // current coverage" and "Keep 75%" in the largest type on the page. A grower
    // acts on the bold line, not on the caveat under it, so the page recommended
    // and retracted in one block. Measured on MEISENZAHL FARMS LLC / Ontario.
    //
    // It matters because the choice is not cosmetic: the engine's own comment
    // records that the BU->EU flip moves the 75->80 cost 27%, so a level endorsed
    // on an assumed structure can be the wrong level, not merely an unpriced one.
    // The guarantee comparison still prints — it is APH x level x price and does
    // not depend on the unit structure.
    const baseOut = unitKnown ? base : {
      ...base,
      text: base.kind === BASE_ACTION.KEEP && base.from != null
        ? "Current coverage level: " + base.from + "% RP"
        : base.to != null ? "Coverage level " + base.to + "% RP — pending" : base.text,
      provisional: true,
      provisionalReason: pb.why || "unit structure not on file",
    };
    return {
      key: u.key, county: titleCounty(u.county), state: u.state || null, practiceLabel: u.practiceLabel || null,
      // ⚠ CARRIED, NEVER BUILT HERE. The coverage stack is the print twin of the
      // on-screen CoverageStackViz and it needs the MPCI, SCO and ECO quotes, which
      // exist only inside the printer that priced them. This module synthesises; it
      // does not re-quote, and a stack rebuilt from the totals below could not carry
      // the per-layer dollars that are the point of drawing one.
      stackHtml: u.stackHtml || null,
      // Same rule, same reason: the level-comparison row is priced in the printer, one
      // quote per level, and carrying it is the only way the report and the quote can
      // show one farm the same comparison.
      barsHtml: u.barsHtml || null,
      status: u && u.priceMissing ? "Price pending"
        : !unitKnown ? "Coverage decision pending unit structure"
        : nothingChanges ? "Keep current coverage" : base.kind === BASE_ACTION.NEW ? "New coverage" : "Change recommended",
      nothingChanges,
      base: baseOut, area, unitKnown,
      premium: cmp.proposed.premium, premiumChange: cmp.diff.premium, currentPremium: cmp.current.premium,
      premiumNote: pb.why,
      acreage: u.acreage || null, acreageText: acreageScopeText(u.acreage, cy, u), acreageSource: acreageSourceText(u.acreage, cy, u),
      estimatedTotal: unitKnown ? estimatedTotal(cmp.proposed.premium, u.acreage) : null,
      reasons, comparison: cmp, history: historyFor(u), selectedBy: u.selectedBy || "silas",
      yieldYears: yieldYears(u), yieldRange: u.yieldRange || null, priceMissing: !!(u && u.priceMissing),
      steps: ((u.engine && u.engine.bandDecision && u.engine.bandDecision.bands) || []).map((b) => ({
        from: pct(b.from), to: pct(b.to), priceable: !!b.priceable && unitKnown, cost: b.priceable && unitKnown ? num(b.margPrem) : null,
        value: b.priceable && unitKnown ? num(b.expIndemnity) : null, result: unitKnown ? stepResult(b) : { text: unitInfo(u).known ? "Premium pending — no acres on record" : "Needs your unit structure", tone: "meh" },
        included: u.proposed && isNum(u.proposed.level) ? Number(u.proposed.level) >= Number(b.to) - EPS : false })),
      aph: num(u.aph), aphYear: u.aphYear || null,
      guarantee: guaranteeFor(u, cmp),
      // ⚠ EACH YEAR'S OWN LATTICE, carried per county so page one can draw last
      // year's stack beside this year's. SCO's top and ECO's bottom moved 86 -> 90
      // for 2027, so drawing both bars on one set of bands paints last year's ECO
      // at half its true height — the defect this repo already paid for once.
      bands: { prior: u.bands2026 || null, quote: u.bands2027 || null },
    };
  });
  const multiState = new Set(recs.map((r) => r.state).filter(Boolean)).size > 1;
  const vm = {
    farm, crop, cropYear: cy,
    title: cy + " " + (crop === "WHEAT" ? "Wheat" : crop) + " Coverage Strategy",
    multiState,
    recommendations: recs,
    important: importantFor(units, price, cy),
    needs: synthesizeNeeds({ units, fsa, cropYear: cy }),
    fsa: fsaSection(fsa),
    market: marketSection(market),
    blocking: (findings || []).filter((f) => f && f.severity === "block").map((f) => f.message),
  };
  vm.contradictions = contradictions(vm, units);
  return vm;
}

// ⚠ A TOTAL IS ONLY AS GOOD AS ITS ACREAGE. Reported acres give an exact total; last
// year's acres give an approximate one, rounded and said to be approximate; an acreage
// nobody supplied gives none at all — a total on an invented 100 acres is not a figure.
export function estimatedTotal(perAcre, acreage) {
  if (!isNum(perAcre) || !acreage || !isNum(acreage.acres) || !(Number(acreage.acres) > 0)) return null;
  const t = Number(perAcre) * Number(acreage.acres);
  if (acreage.source === ENGINE.ACRE.REPORTED) return { exact: true, value: Math.round(t), text: "$" + Math.round(t).toLocaleString("en-US") };
  if (acreage.source === ENGINE.ACRE.PRIOR_YEAR || acreage.source === ENGINE.ACRE.PLANNED)
    return { exact: false, value: Math.round(t / 10) * 10, text: "about $" + (Math.round(t / 10) * 10).toLocaleString("en-US") };
  return null;
}

// ── CONTRADICTIONS — the report must not say something its own data refutes ──
//
// Each check reads the view model against the facts it was built from and returns a
// plain statement of the disagreement. The renderer prints them as a DRAFT banner, so
// a contradictory report is never handed over looking finished; the checker asserts
// every one of them fires on the shape it exists for.
export function contradictions(vm, units) {
  const out = [];
  const add = (key, msg) => out.push({ key, message: msg });
  const byKey = new Map((units || []).map((u) => [u.key, u]));
  for (const r of vm.recommendations || []) {
    const u = byKey.get(r.key) || {};
    const cur = u.current ? num(u.current.level) : null, prop = u.proposed ? num(u.proposed.level) : null;
    if (r.base.kind === BASE_ACTION.KEEP && !same(cur, prop))
      add("keep-but-changes", r.county + ": says keep the coverage level, but the proposed level differs from the current one.");
    if ((r.base.kind === BASE_ACTION.INCREASE || r.base.kind === BASE_ACTION.DECREASE) && same(cur, prop))
      add("change-but-same", r.county + ": says the coverage level changes, but it does not.");
    if (r.nothingChanges && r.area.changes)
      add("hold-all-but-area-changes", r.county + ": says keep current coverage while SCO/ECO changes.");
    if (/keep current coverage|no change/i.test(r.status) && (r.base.kind !== BASE_ACTION.KEEP || r.area.changes))
      add("status-contradicts", r.county + ": the headline says nothing changes, but something does.");
    if (r.base.kind === BASE_ACTION.INCREASE && r.reasons && /keep|stays at/i.test(String(r.reasons.why || "")))
      add("why-contradicts", r.county + ": recommends a higher level while the reason says to keep it.");
    if (r.premium === 0 && !(u.proposed && u.proposed.premium && u.proposed.premium.total === 0))
      add("missing-premium-as-zero", r.county + ": a missing premium is shown as $0.");
    if (r.estimatedTotal && r.estimatedTotal.exact && !(r.acreage && r.acreage.source === ENGINE.ACRE.REPORTED))
      add("estimated-acres-exact-total", r.county + ": an exact total premium is shown on acres that were not reported.");
    // A premium on a unit structure nobody stated is a figure for a policy the grower may not hold.
    if (!unitInfo(u).known && (r.premium != null || r.currentPremium != null || r.premiumChange != null || r.estimatedTotal != null
        || (r.steps || []).some((s) => s.cost != null || s.value != null)))
      add("premium-without-unit", r.county + ": a premium is shown although the unit structure is not on file.");
    if (u && u.acreage && u.acreage.source === ENGINE.ACRE.ASSUMED
        && (r.premium != null || r.currentPremium != null || r.premiumChange != null || r.estimatedTotal != null
            || (r.steps || []).some((s) => s.cost != null || s.value != null)))
      add("premium-on-assumed-acres", r.county + ": a premium is shown on an acreage nobody supplied.");
    // The parts of a change must add up to the change they explain.
    const sp = r.comparison && r.comparison.diff && r.comparison.diff.split;
    if (sp && isNum(r.premiumChange) && Math.abs(sp.parts.reduce((a, p) => a + p.amount, 0) - r.premiumChange) > 0.02)
      add("split-does-not-sum", r.county + ": the parts of the premium change do not add up to the change.");
    // A unit named as enterprise/basic must be the unit the proposal is priced on.
    if (unitInfo(u).known && unitInfo(u).unit && u.proposed && u.proposed.unit && u.proposed.unit !== unitInfo(u).unit)
      add("unit-mismatch", r.county + ": the premium is priced on a different unit structure from the one named.");
  }
  // The FSA column names the program with the higher estimate — never one that is lower.
  if (vm.fsa) for (const row of vm.fsa.rows || []) {
    if (isNum(row.plc) && isNum(row.arc)) {
      const gap = row.arc - row.plc, tie = isNum(vm.fsa.tieBand) ? vm.fsa.tieBand : 0.5;
      if ((row.lean === "PLC" && gap > EPS) || (row.lean === "ARC-CO" && gap < -EPS) || (row.lean === FSA_EVEN && Math.abs(gap) > tie + EPS))
        add("fsa-lean-contradicts", row.county + ": the FSA column names a program whose estimate is not the higher one.");
    }
    // A county the payment yield decides must say so, with the engine's own breakeven —
    // never a bare winner, which is the 60 bu default returning by another route.
    if (row.readState === "threshold" && !(isNum(row.breakeven) && String(row.lean).indexOf("PLC above " + row.breakeven + " bu") === 0))
      add("fsa-condition-dropped", row.county + ": the FSA answer depends on the farm's payment yield but the column names a winner.");
  }
  const p = (vm.important || []).find((x) => x.key === "price");
  if (p && p.tone === PRICE_TONE.FINAL && /estimat|tracking|\best\b|discovery (window )?open/i.test(String(p.note || "") + " " + String(p.detail || "")))
    add("final-price-called-estimate", "The projected price is final but is described as an estimate.");
  if (p && p.tone && p.tone !== PRICE_TONE.FINAL && /\bfinal\b/i.test(String(p.note || "")))
    add("estimate-price-called-final", "The projected price is not final but is described as final.");
  if (vm.fsa && vm.fsa.preliminary && !/preliminary/i.test(vm.fsa.heading))
    add("fsa-preliminary-as-final", "The FSA analysis rests on estimates (a forecast price, unpublished county yields, or a payment yield not on file) but is not labelled preliminary.");
  if (vm.market && !/not recommendations to sell/i.test(vm.market.subheading || ""))
    add("market-as-recommendation", "Futures reference levels are not labelled as reference levels only.");
  const kinds = (vm.needs || []).map((x) => x.kind);
  if (new Set(kinds).size !== kinds.length) add("duplicate-request", "The same request to the grower appears more than once.");
  return out;
}

// The customer-visible text of a rendered standard report, scanned for engine terms.
export function jargonIn(text) { return findJargon(text); }
